import { useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { useIntl, defineMessages } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Input } from 'semantic-ui-react';
import { v4 as uuid } from 'uuid';
import { withBlockExtensions } from '@plone/volto/helpers/Extensions';
import SidebarPortal from '@plone/volto/components/manage/Sidebar/SidebarPortal';
import { BlockDataForm } from '@plone/volto/components/manage/Form';
import { setUIState } from '@plone/volto/actions/form/form';

import TabBlocksEditor from './TabBlocksEditor';
import View from './View';
import { TabsSchema } from './schema';

const messages = defineMessages({
  sidebarTitle: {
    id: 'CSJ tabs sidebar title',
    defaultMessage: 'Pestanyes CSJ',
  },
  sidebarHelp: {
    id: 'CSJ tabs sidebar help',
    defaultMessage:
      'Edita el títol de la pestanya activa al panell. El contingut s’edita amb blocs Slate a sota.',
  },
  addTab: {
    id: 'CSJ tabs add tab',
    defaultMessage: 'Afegir pestanya',
  },
  removeTab: {
    id: 'CSJ tabs remove tab',
    defaultMessage: 'Eliminar pestanya',
  },
  tabTitle: {
    id: 'CSJ tab title inline',
    defaultMessage: 'Títol de la pestanya',
  },
  tabTitlePlaceholder: {
    id: 'CSJ tab title placeholder',
    defaultMessage: 'Títol de la pestanya',
  },
  tabIcon: {
    id: 'CSJ tab icon',
    defaultMessage: 'Icona / imatge de pestanya',
  },
});

const emptyTab = () => {
  const blockId = uuid();
  return {
    '@id': uuid(),
    title: '',
    blocks: {
      [blockId]: { '@type': 'slate' },
    },
    blocks_layout: { items: [blockId] },
  };
};

const tabIconSchema = (intl) => ({
  title: intl.formatMessage(messages.tabIcon),
  fieldsets: [{ id: 'default', title: 'Default', fields: ['icon'] }],
  properties: {
    icon: {
      title: intl.formatMessage(messages.tabIcon),
      widget: 'object_browser',
      mode: 'image',
      return: 'single',
      selectableTypes: ['Image'],
      initialPath: '/recursos',
      selectedItemAttrs: ['@type', 'title', 'image_field', 'image_scales'],
    },
  },
  required: [],
});

const Edit = (props) => {
  const {
    data,
    block,
    onChangeBlock,
    selected,
    pathname,
    manage,
    metadata,
    properties,
  } = props;
  const intl = useIntl();
  const dispatch = useDispatch();
  const tabBlockSelected = useSelector((state) => state.form.ui.gridSelected);
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const schema = TabsSchema({ intl });
  const tabs = data?.tabs || [];
  const activeTab = tabs[activeTabIndex] || tabs[0];

  const setTabBlockSelected = (id) => {
    dispatch(setUIState({ gridSelected: id }));
  };

  const updateTabs = (nextTabs, nextActiveIndex = activeTabIndex) => {
    onChangeBlock(block, { ...data, tabs: nextTabs });
    if (nextActiveIndex >= nextTabs.length) {
      setActiveTabIndex(Math.max(0, nextTabs.length - 1));
    } else {
      setActiveTabIndex(nextActiveIndex);
    }
    setTabBlockSelected(null);
  };

  const onAddTab = () => {
    const nextTabs = [...tabs, emptyTab()];
    updateTabs(nextTabs, nextTabs.length - 1);
  };

  const onRemoveTab = () => {
    if (!tabs.length) {
      return;
    }
    const nextTabs = tabs.filter((_, index) => index !== activeTabIndex);
    updateTabs(nextTabs);
  };

  const onChangeTabTitle = (value) => {
    const nextTabs = tabs.map((tab, index) =>
      index === activeTabIndex ? { ...tab, title: value } : tab,
    );
    onChangeBlock(block, { ...data, tabs: nextTabs });
  };

  const onChangeTabIcon = (value) => {
    const nextTabs = tabs.map((tab, index) =>
      index === activeTabIndex ? { ...tab, icon: value } : tab,
    );
    onChangeBlock(block, { ...data, tabs: nextTabs });
  };

  const showTabIcon = data?.variant === 'icon' || data?.variant === 'image';
  const tabIconFormSchema = tabIconSchema(intl);

  if (!selected) {
    return (
      <View
        data={data}
        id={block}
        path={pathname}
        metadata={metadata || properties}
      />
    );
  }

  return (
    <>
      <div
        className={cx(
          'csj-tabs-edit',
          data?.variant && `tabs-variant-${data.variant}`,
        )}
        role="presentation"
        onClick={(event) => {
          if (!event.block) {
            setTabBlockSelected(null);
          }
        }}
      >
        {data?.section_heading ? <h3>{data.section_heading}</h3> : null}
        <div className="csj-tabs-edit-toolbar">
          <div className="csj-tabs-edit-tabs">
            {tabs.map((tab, index) => (
              <Button
                key={tab['@id'] || `${tab.title || 'tab'}-${index}`}
                type="button"
                size="small"
                basic={index !== activeTabIndex}
                primary={index === activeTabIndex}
                onClick={() => {
                  setActiveTabIndex(index);
                  setTabBlockSelected(null);
                }}
              >
                {tab.title || intl.formatMessage(messages.tabTitlePlaceholder)}
              </Button>
            ))}
          </div>
          <div className="csj-tabs-edit-actions">
            <Button compact size="mini" type="button" onClick={onAddTab}>
              {intl.formatMessage(messages.addTab)}
            </Button>
            {tabs.length ? (
              <Button
                compact
                size="mini"
                type="button"
                negative
                onClick={onRemoveTab}
              >
                {intl.formatMessage(messages.removeTab)}
              </Button>
            ) : null}
          </div>
        </div>
        {activeTab ? (
          <>
            <div className="csj-tabs-edit-title">
              <label htmlFor={`${block}-tab-title`}>
                {intl.formatMessage(messages.tabTitle)}
              </label>
              <Input
                id={`${block}-tab-title`}
                fluid
                value={activeTab.title || ''}
                placeholder={intl.formatMessage(messages.tabTitlePlaceholder)}
                onChange={(_event, { value }) => onChangeTabTitle(value)}
              />
            </div>
            <TabBlocksEditor
              tab={activeTab}
              tabIndex={activeTabIndex}
              data={data}
              block={block}
              onChangeBlock={onChangeBlock}
              pathname={pathname}
              selected={selected}
              manage={manage}
              metadata={metadata}
              properties={properties}
              tabBlockSelected={tabBlockSelected}
              setTabBlockSelected={setTabBlockSelected}
            />
          </>
        ) : null}
      </div>
      <SidebarPortal selected={selected && !tabBlockSelected}>
        <div className="csj-tabs-sidebar">
          <h2>{intl.formatMessage(messages.sidebarTitle)}</h2>
          <p>{intl.formatMessage(messages.sidebarHelp)}</p>
          <BlockDataForm
            schema={schema}
            title={schema.title}
            onChangeField={(id, value) => {
              onChangeBlock(block, { ...data, [id]: value });
            }}
            formData={data}
            block={block}
            onChangeBlock={onChangeBlock}
          />
          {showTabIcon && activeTab ? (
            <BlockDataForm
              schema={tabIconFormSchema}
              title={tabIconFormSchema.title}
              onChangeField={(_id, value) => onChangeTabIcon(value)}
              formData={activeTab}
              block={`${block}-tab-icon`}
              onChangeBlock={(_id, nextTab) => {
                const nextTabs = (data.tabs || []).map((tab, index) =>
                  index === activeTabIndex ? nextTab : tab,
                );
                onChangeBlock(block, { ...data, tabs: nextTabs });
              }}
            />
          ) : null}
        </div>
      </SidebarPortal>
    </>
  );
};

Edit.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  block: PropTypes.string.isRequired,
  onChangeBlock: PropTypes.func.isRequired,
  selected: PropTypes.bool.isRequired,
  pathname: PropTypes.string.isRequired,
  manage: PropTypes.bool.isRequired,
  metadata: PropTypes.objectOf(PropTypes.any),
  properties: PropTypes.objectOf(PropTypes.any),
};

export default withBlockExtensions(Edit);
